import React from 'react';
import styled from 'styled-components';

const FooterContainer = styled.footer`
  background-color: ${props => props.theme.colors.footerBackground};
  padding: 4.5rem 5% 1.8rem;
  border-top: 1px solid ${props => props.theme.colors.borderColor};
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1.8rem;
  
  @media (max-width: 991px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FooterCol = styled.div``;

const FooterLogo = styled.div`
  font-size: 1.62rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: 1.35rem;
  
  span {
    color: ${props => props.theme.colors.accent};
  }
`;

const FooterDescription = styled.p`
  color: ${props => props.theme.colors.secondaryText};
  margin-bottom: 1.8rem;
  line-height: 1.6;
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 0.9rem;
`;

const SocialIcon = styled.a`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => props.theme.colors.iconBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  color: ${props => props.theme.colors.iconColor};
  
  &:hover, &:focus {
    background: ${props => props.theme.colors.accent};
    transform: translateY(-4.5px);
    color: white;
    text-decoration: none;
    outline: none;
  }
  
  &:focus {
    box-shadow: 0 0 0 2.7px ${props => props.theme.colors.accent}80;
  }
  
  svg {
    color: inherit;
    font-size: 1.08rem;
    width: 1.08rem;
    height: 1.08rem;
  }
  
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    
    &:hover, &:focus {
      transform: none;
    }
  }
`;

const FooterTitle = styled.h3`
  font-size: 1.08rem;
  margin-bottom: 1.35rem;
  color: ${props => props.theme.colors.text};
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 36px;
    height: 2.7px;
    background: ${props => props.theme.colors.accent};
    margin-top: 0.72rem;
    border-radius: 1.8px;
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding-left: 0;
  margin: 0;
`;

const FooterLink = styled.li`
  margin-bottom: 0.9rem;
  
  a {
    color: ${props => props.theme.colors.secondaryText};
    transition: color 0.3s ease;
    text-decoration: none;
    display: inline-block;
    padding: 0.18rem 0;
    
    &:hover, &:focus {
      color: ${props => props.theme.colors.accent};
      text-decoration: underline;
    }
    
    &:focus {
      outline: 1.8px solid ${props => props.theme.colors.accent};
      outline-offset: 1.8px;
    }
    
    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }
`;

const ContactInfo = styled.div`
  margin-bottom: 0.9rem;
  display: flex;
  align-items: flex-start;
  
  svg {
    margin-right: 0.9rem;
    color: ${props => props.theme.colors.accent};
    font-size: 1.08rem;
    margin-top: 0.18rem;
    flex-shrink: 0;
    width: 1.08rem;
    height: 1.08rem;
  }
`;

const ContactText = styled.p`
  color: ${props => props.theme.colors.secondaryText};
  margin: 0;
`;

const ContactLink = styled.a`
  color: ${props => props.theme.colors.secondaryText};
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover, &:focus {
    color: ${props => props.theme.colors.accent};
    text-decoration: underline;
  }
  
  &:focus {
    outline: 1.8px solid ${props => props.theme.colors.accent};
    outline-offset: 1.8px;
  }
  
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 2.7rem;
  margin-top: 2.7rem;
  border-top: 1px solid ${props => props.theme.colors.borderColor};
  color: ${props => props.theme.colors.secondaryText};
  font-size: 0.81rem;
`;

// Composant pour les icônes avec attributs d'accessibilité
const LocationIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    strokeLinecap="round"
    strokeLinejoin="round"
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

const PhoneIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    strokeLinecap="round"
    strokeLinejoin="round"
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
  </svg>
);

const EmailIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    strokeLinecap="round"
    strokeLinejoin="round"
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
    <polyline points="22,6 12,13 2,6"></polyline>
  </svg>
);

// Composants pour les icônes de réseaux sociaux
const FacebookIcon = () => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 320 512"
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"></path>
  </svg>
);

const TwitterIcon = () => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 512 512"
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M459.37 151.716c.325 4.548.325 9.097.325 13.645 0 138.72-105.583 298.558-298.558 298.558-59.452 0-114.68-17.219-161.137-47.106 8.447.974 16.568 1.299 25.34 1.299 49.055 0 94.213-16.568 130.274-44.832-46.132-.975-84.792-31.188-98.112-72.772 6.498.974 12.995 1.624 19.818 1.624 9.421 0 18.843-1.3 27.614-3.573-48.081-9.747-84.143-51.98-84.143-102.985v-1.299c13.969 7.797 30.214 12.67 47.431 13.319-28.264-18.843-46.781-51.005-46.781-87.391 0-19.492 5.197-37.36 14.294-52.954 51.655 63.675 129.3 105.258 216.365 109.807-1.624-7.797-2.599-15.918-2.599-24.04 0-57.828 46.782-104.934 104.934-104.934 30.213 0 57.502 12.67 76.67 33.137 23.715-4.548 46.456-13.32 66.599-25.34-7.798 24.366-24.366 44.833-46.132 57.827 21.117-2.273 41.584-8.122 60.426-16.243-14.292 20.791-32.161 39.308-52.628 54.253z"></path>
  </svg>
);

const LinkedinIcon = () => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 448 512"
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z"></path>
  </svg>
);

const InstagramIcon = () => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 448 512"
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"></path>
  </svg>
);

// Microdata Schema.org pour l'organisation
const SchemaOrg = () => (
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Rank eed",
      "url": "https://rankeed.fr",
      "logo": "https://rankeed.fr/logo.png",
      "description": "Agence web spécialisée dans la création et la refonte de sites web professionnels.",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "La Rochelle",
        "postalCode": "17000",
        "addressCountry": "FR"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+33766171996",
        "contactType": "customer service",
        "email": "contact.rankeed@gmail.com"
      },
      "sameAs": [
        "https://www.facebook.com/rankeed",
        "https://twitter.com/rankeed",
        "https://www.linkedin.com/company/rankeed",
        "https://www.instagram.com/rankeed"
      ]
    })}
  </script>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer role="contentinfo">
      {/* Schema.org microdata pour le SEO */}
      <SchemaOrg />

      <FooterGrid>
        <FooterCol>
          <FooterLogo>
            Rank<span>eed</span>
          </FooterLogo>
          <FooterDescription>
            Nous sommes une agence web spécialisée dans la création et la refonte de sites web professionnels, avec une passion pour le design et des performances optimales.
          </FooterDescription>
          <SocialIcons>

            <SocialIcon
              href="https://www.linkedin.com/company/rankeed"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Suivez-nous sur LinkedIn"
            >
              <LinkedinIcon />
            </SocialIcon>
          </SocialIcons>
        </FooterCol>

        <FooterCol>
          <FooterTitle id="footer-links-title">Liens rapides</FooterTitle>
          <FooterLinks aria-labelledby="footer-links-title">
            <FooterLink>
              <a href="#services" aria-label="Découvrir nos services de création de sites web">Services</a>
            </FooterLink>
            <FooterLink>
              <a href="#portfolio" aria-label="Voir notre portfolio de sites web créés">Portfolio</a>
            </FooterLink>
            <FooterLink>
              <a href="#contact" aria-label="Nous contacter pour votre projet de site web">Contact</a>
            </FooterLink>
            <FooterLink>
              <a href="/mentions-legales" aria-label="Consultez nos mentions légales">Mentions légales</a>
            </FooterLink>
            <FooterLink>
              <a href="/politique-confidentialite" aria-label="Consultez notre politique de confidentialité">Confidentialité</a>
            </FooterLink>
          </FooterLinks>
        </FooterCol>

        <FooterCol>
          <FooterTitle id="footer-contact-title">Contact</FooterTitle>
          <div aria-labelledby="footer-contact-title">
            <ContactInfo>
              <LocationIcon />
              <ContactLink
                href="https://maps.google.com/?q=La+Rochelle+17000+France"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Notre localisation sur Google Maps : La Rochelle 17000, France"
              >
                La Rochelle 17000, France
              </ContactLink>
            </ContactInfo>
            <ContactInfo>
              <PhoneIcon />
              <ContactLink
                href="tel:+33766171996"
                aria-label="Appeler le numéro de téléphone +33 7 66 17 19 96"
              >
                +33 7 66 17 19 96
              </ContactLink>
            </ContactInfo>
            <ContactInfo>
              <EmailIcon />
              <ContactLink
                href="mailto:contact.rankeed@gmail.com"
                aria-label="Envoyer un email à contact.rankeed@gmail.com"
              >
                contact.rankeed@gmail.com
              </ContactLink>
            </ContactInfo>
          </div>
        </FooterCol>
      </FooterGrid>

      <Copyright>
        © {currentYear} Rankeed. Tous droits réservés.
      </Copyright>
    </FooterContainer>
  );
};

export default Footer;